import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { faChevronLeft, faChevronRight, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import Select from 'react-select'
import { useNewTranslation, useWindowSize } from 'hooks/index'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import qs from 'query-string'
import Button from 'components/Shared/Button/OldButton'
import ButtonFa from 'components/ButtonFa'
import { theme } from 'theme'
import { SUBMISSION_VIEW_ENUM } from 'state/submissions'
import { selectTasks } from 'state/tasks'
import {
    selectShowSidebar,
    selectSidebarStep,
    selectSelectedStepSummary,
    selectSelectedSubmissionView,
    selectSelectedRegistrationView,
    selectSidebarTask,
    SUBMISSION_VIEW_LABEL,
    setShowSidebar,
    resetMultiSelectSetting,
    selectSelectedCampaignSummary,
    REGISTRATION_VIEW_ENUM,
    REGISTRATION_VIEW_LABEL,
    selectSidebarStepUuid,
    selectSelectedOverview,
    OVERVIEW_ENUM,
    setVoteTypeFilter,
    setPartiposterTagFilter,
    setResubmittedTypeFilter,
    setSubmissionChips,
} from 'state/review'
import InfoTooltip from 'components/InfoTooltip'
import { STEP_TYPE_ENUM } from 'services/api/constants'
import { Container } from './styles'
import Text from 'particles/Text'
import Divider from 'components/Shared/Divider'
import {
    closeModal,
    selectIsEngagementStepTypeHelper,
    selectIsUrlOrScreenshotSubmissionStepTypeHelper,
} from 'state/constants'
import styled from 'particles/themes/styled'
import { SEARCH_PARAMS, SearchParamObject, getSearchParams } from 'components/utils/url'

export const REGISTRATION_VIEW_TYPES: REGISTRATION_VIEW_ENUM[] = [
    REGISTRATION_VIEW_ENUM.PENDING,
    REGISTRATION_VIEW_ENUM.WAITLISTED,
    REGISTRATION_VIEW_ENUM.AWAITING_RESPONSE,
    REGISTRATION_VIEW_ENUM.ACCEPTED_INVITES,
    REGISTRATION_VIEW_ENUM.JOINED_CAMPAIGN,
    REGISTRATION_VIEW_ENUM.DECLINED_INVITES,
]

export const SINGLE_TASK_VIEW_TYPES: SUBMISSION_VIEW_ENUM[] = [
    SUBMISSION_VIEW_ENUM.FOR_REVIEW,
    SUBMISSION_VIEW_ENUM.PENDING,
    SUBMISSION_VIEW_ENUM.EDITS_REQUESTED,
    SUBMISSION_VIEW_ENUM.COMPLETED,
]

const StatusDefinition: React.FC<{ title: string; description: string }> = ({ title, description }) => (
    <>
        <Text scale="bodyS700" style={{ marginBottom: theme.spacing.xxxxs }} color="primaryInverse">
            {title}
        </Text>
        <Text scale="caption400" style={{ marginBottom: theme.spacing.xxs }} color="primaryInverse">
            {description}
        </Text>
    </>
)

const NavSidebar: React.FC<{ show: boolean }> = ({ show }) => {
    const dispatch = useDispatch()
    const [newT] = useNewTranslation()
    const isEngagementStepType = useSelector(selectIsEngagementStepTypeHelper)
    const isUrlOrScreenshotSubmissionStepType = useSelector(selectIsUrlOrScreenshotSubmissionStepTypeHelper)
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const tasks = useSelector(selectTasks)
    const sidebarTask = useSelector(selectSidebarTask)
    const sidebarStepUuid = useSelector(selectSidebarStepUuid)
    const selectedStep = useSelector(selectSidebarStep)
    const selectedStepSummary = useSelector(selectSelectedStepSummary)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const selectedRegistrationView = useSelector(selectSelectedRegistrationView)
    const selectedOverview = useSelector(selectSelectedOverview)
    const location = useLocation()
    const { viewType } = qs.parse(location.search) as SearchParamObject
    const history = useHistory()

    const [isRegistrationSectionOpen, setIsRegistrationSectionOpen] = useState<boolean>(
        viewType ? REGISTRATION_VIEW_TYPES.includes(viewType as REGISTRATION_VIEW_ENUM) : false,
    )
    const [isOverviewSectionOpen, setIsOverviewSectionOpen] = useState<boolean>(true)
    const [isSingleTaskSectionOpen, setIsSingleTaskSectionOpen] = useState<boolean>(
        !isRegistrationSectionOpen
            ? [...SINGLE_TASK_VIEW_TYPES, SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS].includes(
                  viewType as SUBMISSION_VIEW_ENUM,
              )
            : false,
    )

    const onHideSidebar = useCallback(() => {
        dispatch(setShowSidebar(false))
    }, [])

    const registrationViews = useMemo(() => [...REGISTRATION_VIEW_TYPES], [])

    const singleTaskViews = useMemo(() => {
        let result = [...SINGLE_TASK_VIEW_TYPES]

        if (!selectedStep) {
            return result
        }

        if (
            [STEP_TYPE_ENUM.POST_TO_IG_POST, STEP_TYPE_ENUM.POST_TO_FB_POST, STEP_TYPE_ENUM.POST_TO_IG_STORY].indexOf(
                selectedStep.type,
            ) >= 0
        ) {
            result = [SUBMISSION_VIEW_ENUM.PENDING, SUBMISSION_VIEW_ENUM.COMPLETED]
        } else if (isEngagementStepType(selectedStep.type) || isUrlOrScreenshotSubmissionStepType(selectedStep.type)) {
            result = [
                SUBMISSION_VIEW_ENUM.PENDING,
                SUBMISSION_VIEW_ENUM.EDITS_REQUESTED,
                SUBMISSION_VIEW_ENUM.COMPLETED,
            ]
        } else if (selectedStep.type === 'purchase_product' || selectedStep.type === 'attend_event') {
            result = [SUBMISSION_VIEW_ENUM.COMPLETED]
        } else if (selectedStep.type === 'custom') {
            const BLOCK_TYPES_WITH_PARTIPOSTER_CONTENT = ['photo_submission', 'text_submission', 'url_submission']
            const submissionBlocks =
                selectedStep.data.blocks &&
                selectedStep.data.blocks.filter(
                    (block: any) => BLOCK_TYPES_WITH_PARTIPOSTER_CONTENT.indexOf(block.type) >= 0,
                )
            if (submissionBlocks.length === 0) {
                result = [SUBMISSION_VIEW_ENUM.COMPLETED]
            }
        }

        return result
    }, [selectedStep, isEngagementStepType, isUrlOrScreenshotSubmissionStepType])

    useEffect(() => {
        // expand navbar base on viewtype, this helps to open navbar after reload then navigating back
        if (REGISTRATION_VIEW_TYPES.includes(viewType as REGISTRATION_VIEW_ENUM)) {
            setIsRegistrationSectionOpen(true)
        } else if (SINGLE_TASK_VIEW_TYPES.includes(viewType as SUBMISSION_VIEW_ENUM)) {
            setIsSingleTaskSectionOpen(true)
        } else if (viewType === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS) {
            setIsOverviewSectionOpen(true)
        }
    }, [viewType])

    useEffect(() => {
        // change to PENDING / COMPLETE / first status when singleTaskViews is changing
        // requirement: change selected status to pending (if available) when step & task change
        const isViewTypeNotInViewList =
            SINGLE_TASK_VIEW_TYPES.includes(selectedSubmissionView as SUBMISSION_VIEW_ENUM) &&
            singleTaskViews.indexOf(selectedSubmissionView as SUBMISSION_VIEW_ENUM) < 0
        if (!viewType || isViewTypeNotInViewList) {
            let submissionView = singleTaskViews[0]
            if (singleTaskViews.indexOf(SUBMISSION_VIEW_ENUM.PENDING) >= 0) {
                submissionView = SUBMISSION_VIEW_ENUM.PENDING
            } else if (singleTaskViews.indexOf(SUBMISSION_VIEW_ENUM.COMPLETED)) {
                submissionView = SUBMISSION_VIEW_ENUM.COMPLETED
            }
            const searchParams = getSearchParams({
                add: [{ key: SEARCH_PARAMS.viewType, value: submissionView }],
            })
            history.replace(searchParams)
            if (isMobileSize) {
                onHideSidebar()
            }
        }
        // viewType is not included as dependency on purpose, it only trigger by singleTaskViews
        // so, it only runs when: selected step is changing / selected task is changing
    }, [singleTaskViews])

    useEffect(() => {
        if (sidebarTask && !sidebarStepUuid) {
            const searchParams = getSearchParams({
                add: [{ key: SEARCH_PARAMS.step, value: sidebarTask.steps[0].uuid }],
            })
            history.replace(searchParams)
        }
    }, [sidebarTask, sidebarStepUuid])

    const StatusDefinitions = useMemo(
        () => [
            {
                title: newT('statusLabel.pending', 'Pending'),
                description: newT(
                    'statusLabel.pendingTooltipDescription',
                    'Partiposters who have registered for your campaign and are pending your review.',
                ),
            },
            {
                title: newT('statusLabel.waitlist', 'Waitlist'),
                description: newT(
                    'statusLabel.waitlistTooltipDescription',
                    'Partiposters in the waitlist will be automatically approved whenever a registration slot becomes available.',
                ),
            },
            {
                title: newT('statusLabel.awaitingResponse', 'Awaiting Response'),
                description: newT(
                    'statusLabel.awaitingResponseTooltipDescription',
                    'Partiposters whose registration has been approved. They can now either accept or decline the campaign invitation.',
                ),
            },
            {
                title: newT('statusLabel.accepted', 'Accepted Invites'),
                description: newT(
                    'statusLabel.acceptedTooltipDescription',
                    'Partiposters who have accepted your invitation to the campaign but have not started on any campaign tasks.',
                ),
            },
            {
                title: newT('statusLabel.joinedCampaign', 'Joined Campaign'),
                description: newT(
                    'statusLabel.joinedCampaignTooltipDescription',
                    'Partiposters who have started on at least one campaign task after accepting your invitation to the campaign.',
                ),
            },
            {
                title: newT('statusLabel.declined', 'Declined Invites'),
                description: newT(
                    'statusLabel.declinedTooltipDescription',
                    'Partiposters who declined your invitation. They will not be joining the campaign.',
                ),
            },
        ],
        [newT],
    )
    const windowSize = useWindowSize()
    const isMobileSize = windowSize <= 768

    if (!sidebarTask || !selectedStep || !selectedStepSummary) return null

    const taskOptions = tasks.map((task: any) => ({
        value: task.uuid,
        label: task.title,
    }))
    const selectedTaskOption = taskOptions.filter((option: any) => option.value === sidebarTask.uuid)[0]
    const handleSelectTaskOption = (option: any) => {
        if (sidebarTask.uuid !== option.value) {
            dispatch(resetMultiSelectSetting())
            dispatch(closeModal())
            dispatch(setVoteTypeFilter([]))
            dispatch(setPartiposterTagFilter([]))
            dispatch(setResubmittedTypeFilter([]))
            dispatch(
                setSubmissionChips({
                    isPartiposterTagsChipOpen: false,
                    isVoteTypeChipOpen: false,
                    isResubmittedTypeChipOpen: false,
                }),
            )
            const stepUuid = tasks.find(task => task.uuid === option.value)?.steps[0].uuid
            if (stepUuid) {
                const searchParams = getSearchParams({
                    add: [
                        { key: SEARCH_PARAMS.task, value: option.value },
                        { key: SEARCH_PARAMS.step, value: stepUuid },
                    ],
                    remove: [SEARCH_PARAMS.reviewId, SEARCH_PARAMS.viewType],
                })
                history.push(searchParams)
            }
        }
    }

    const stepOptions = sidebarTask.steps.map((step: any) => ({
        value: step.uuid,
        label: step.data.title,
    }))
    const selectedOption = stepOptions.filter((option: any) => option.value === selectedStep.uuid)[0]
    const handleSelectOption = (option: any) => {
        if (selectedStep.uuid !== option.value) {
            dispatch(resetMultiSelectSetting())
            dispatch(closeModal())
            dispatch(setVoteTypeFilter([]))
            dispatch(setPartiposterTagFilter([]))
            dispatch(setResubmittedTypeFilter([]))
            dispatch(
                setSubmissionChips({
                    isPartiposterTagsChipOpen: false,
                    isVoteTypeChipOpen: false,
                    isResubmittedTypeChipOpen: false,
                }),
            )
            const searchParams = getSearchParams({
                add: [{ key: SEARCH_PARAMS.step, value: option.value }],
                remove: [SEARCH_PARAMS.reviewId, SEARCH_PARAMS.viewType],
            })
            history.push(searchParams)
        }
    }

    const getCounts = (viewType: SUBMISSION_VIEW_ENUM | REGISTRATION_VIEW_ENUM): number => {
        if (!campaignSummary) return 0
        const { registration_enable, registration_result_summary } = campaignSummary
        if (registration_enable && !registration_result_summary) return 0
        switch (viewType) {
            case REGISTRATION_VIEW_ENUM.PENDING:
                return registration_result_summary ? registration_result_summary.pending_count : 0
            case REGISTRATION_VIEW_ENUM.WAITLISTED:
                return registration_result_summary ? registration_result_summary.waitlisted_count : 0
            case REGISTRATION_VIEW_ENUM.AWAITING_RESPONSE:
                return registration_result_summary ? registration_result_summary.awaiting_response_count : 0
            case REGISTRATION_VIEW_ENUM.ACCEPTED_INVITES:
                return registration_result_summary ? registration_result_summary.user_accepted_count : 0
            case REGISTRATION_VIEW_ENUM.JOINED_CAMPAIGN:
                return registration_result_summary ? registration_result_summary.joined_count : 0
            case REGISTRATION_VIEW_ENUM.DECLINED_INVITES:
                return registration_result_summary ? registration_result_summary.declined_invites_count : 0
            default:
                return selectedStepSummary.submission_progress_summary[`${viewType}_count`]
        }
    }

    const handleRegistrationClick = (registrationView: REGISTRATION_VIEW_ENUM) => {
        // to avoid push same search params over & over
        if (
            registrationView !== selectedRegistrationView ||
            REGISTRATION_VIEW_TYPES.includes(viewType as REGISTRATION_VIEW_ENUM)
        ) {
            dispatch(resetMultiSelectSetting())
            dispatch(closeModal())
            const searchParams = getSearchParams({
                add: [{ key: SEARCH_PARAMS.viewType, value: registrationView }],
                remove: [SEARCH_PARAMS.reviewId],
            })
            history.push(searchParams)
        }
        if (isMobileSize) {
            onHideSidebar()
        }
    }

    const handleBatchSubmissionClick = () => {
        const searchParams = getSearchParams({
            add: [{ key: SEARCH_PARAMS.viewType, value: SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS }],
            remove: [SEARCH_PARAMS.reviewId],
        })

        // current fix for redirecting again if we click the same button while active
        const currSearchParams = location.search
        if (currSearchParams !== searchParams) {
            dispatch(resetMultiSelectSetting())
            dispatch(closeModal())
            dispatch(setVoteTypeFilter([]))
            dispatch(setPartiposterTagFilter([]))
            dispatch(setResubmittedTypeFilter([]))
            dispatch(
                setSubmissionChips({
                    isPartiposterTagsChipOpen: false,
                    isVoteTypeChipOpen: false,
                    isResubmittedTypeChipOpen: false,
                }),
            )
            history.push(searchParams)
        }

        if (isMobileSize) {
            onHideSidebar()
        }
    }

    const handleSubmissionClick = (submissionView: SUBMISSION_VIEW_ENUM) => {
        // to avoid push same search params over & over
        if (
            selectedSubmissionView !== submissionView ||
            REGISTRATION_VIEW_TYPES.includes(viewType as REGISTRATION_VIEW_ENUM)
        ) {
            dispatch(resetMultiSelectSetting())
            dispatch(closeModal())
            dispatch(setVoteTypeFilter([]))
            dispatch(setPartiposterTagFilter([]))
            dispatch(setResubmittedTypeFilter([]))
            dispatch(
                setSubmissionChips({
                    isPartiposterTagsChipOpen: false,
                    isVoteTypeChipOpen: false,
                    isResubmittedTypeChipOpen: false,
                }),
            )
            const searchParams = getSearchParams({
                add: [{ key: SEARCH_PARAMS.viewType, value: submissionView }],
                remove: [SEARCH_PARAMS.reviewId],
            })
            history.push(searchParams)
        }
        if (isMobileSize) {
            onHideSidebar()
        }
    }

    const handleOverviewClick = (overviewType: OVERVIEW_ENUM) => {
        dispatch(resetMultiSelectSetting())
        dispatch(closeModal())
        const searchParams = getSearchParams({
            add: [
                {
                    key: SEARCH_PARAMS.viewType,
                    value: overviewType,
                },
            ],
            remove: [SEARCH_PARAMS.reviewId],
        })
        history.push(searchParams)
        if (isMobileSize) {
            onHideSidebar()
        }
    }

    return (
        <Container show={show}>
            {isMobileSize ? (
                <div className="mobile-navsidebar-top">
                    <ButtonFa icon={faChevronLeft} iconSize="xs" sx={{ textAlign: 'right' }} onClick={onHideSidebar} />
                    <div className="mobile-navsidebar-top-divider"></div>
                </div>
            ) : (
                <ButtonFa
                    icon={faChevronLeft}
                    iconSize="xs"
                    sx={{ textAlign: 'right' }}
                    mr={`-${theme.spacing.xxs}`}
                    onClick={onHideSidebar}
                />
            )}
            <div className="title-block " onClick={() => setIsOverviewSectionOpen(!isOverviewSectionOpen)}>
                <Text scale="bodyS700" color="secondary">
                    {newT('sidebar.submission.overview', 'OVERVIEW').toUpperCase()}
                </Text>
                <ButtonFa
                    icon={isOverviewSectionOpen ? faChevronDown : faChevronUp}
                    iconSize="xs"
                    p={`${theme.spacing.xxxs} ${theme.spacing.xxs}`}
                />
            </div>
            {isOverviewSectionOpen ? (
                <div className="options-block">
                    <Button
                        width="100%"
                        bg={
                            selectedOverview === OVERVIEW_ENUM.REPORT ? theme.colors.grandis : theme.newColors.neutral50
                        }
                        textProps={{ color: theme.colors.black, textAlign: 'left' }}
                        onClick={() => handleOverviewClick(OVERVIEW_ENUM.REPORT)}
                    >
                        {newT('submission.review.campaignReportTitle')}
                    </Button>
                    <Button
                        width="100%"
                        bg={
                            selectedOverview === OVERVIEW_ENUM.OVERVIEW
                                ? theme.colors.grandis
                                : theme.newColors.neutral50
                        }
                        textProps={{ color: theme.colors.black, textAlign: 'left' }}
                        onClick={() => handleOverviewClick(OVERVIEW_ENUM.OVERVIEW)}
                    >
                        {newT('submission.review.campaignOverviewTitle')}
                    </Button>
                </div>
            ) : null}
            <Divider my={theme.newSpacing.xxs} />

            {campaignSummary && campaignSummary.registration_enable ? (
                <>
                    <div
                        className="title-block"
                        onClick={() => setIsRegistrationSectionOpen(!isRegistrationSectionOpen)}
                    >
                        <Text scale="bodyS700" color="secondary">
                            {newT('sidebar.submission.registration', 'REGISTRATION').toUpperCase()}
                        </Text>
                        <ButtonFa
                            icon={isRegistrationSectionOpen ? faChevronDown : faChevronUp}
                            iconSize="xs"
                            p={`${theme.spacing.xxxs} ${theme.spacing.xxs}`}
                        />
                    </div>
                    {isRegistrationSectionOpen ? (
                        <div className="steps-block">
                            <div className="steps-title-block">
                                <Text scale="bodyS700" color="secondary">
                                    {newT('sidebar.submission.status', 'STATUSES')}
                                </Text>
                                <InfoTooltip
                                    title={newT('statusLabel.statusDefinitionTooltipHeader')}
                                    label={StatusDefinitions.map((status, index) => (
                                        <StatusDefinition key={index} {...status} />
                                    ))}
                                    tooltipPlacement="right-start"
                                />
                            </div>
                            <div className="options-block">
                                {registrationViews.map(registrationView => {
                                    const isSelected = registrationView === selectedRegistrationView
                                    return (
                                        <Button
                                            key={registrationView}
                                            width="100%"
                                            bg={isSelected ? theme.colors.grandis : theme.newColors.neutral50}
                                            textProps={{ color: theme.colors.black, textAlign: 'left' }}
                                            px={theme.spacing.xs}
                                            onClick={() => handleRegistrationClick(registrationView)}
                                        >
                                            {`${REGISTRATION_VIEW_LABEL[registrationView]} (${getCounts(
                                                registrationView,
                                            )})`}
                                        </Button>
                                    )
                                })}
                            </div>
                        </div>
                    ) : null}
                    <Divider my={theme.newSpacing.xxs} />
                </>
            ) : null}

            <div className="title-block " onClick={() => setIsSingleTaskSectionOpen(!isSingleTaskSectionOpen)}>
                <Text scale="bodyS700" color="secondary">
                    {newT('submission.review.submissionTitle').toUpperCase()}
                </Text>
                <ButtonFa
                    icon={isSingleTaskSectionOpen ? faChevronDown : faChevronUp}
                    iconSize="xs"
                    p={`${theme.spacing.xxxs} ${theme.spacing.xxs}`}
                />
            </div>
            {isSingleTaskSectionOpen ? (
                <div className="single-tasks-options-block">
                    <Button
                        width="100%"
                        bg={
                            !selectedRegistrationView &&
                            selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS
                                ? theme.colors.grandis
                                : theme.newColors.neutral50
                        }
                        textProps={{ color: theme.colors.black, textAlign: 'left' }}
                        onClick={handleBatchSubmissionClick}
                    >
                        {`${newT('submission.review.allSubmissionsTitle', 'All submissions')} (${
                            campaignSummary?.total_content_submissions || 0
                        })`}
                    </Button>
                    {tasks.length > 1 ? (
                        <>
                            <Text scale="bodyS700" color="secondary">
                                {newT('campaign.section.tasks', {
                                    defaultValue: 'TASK',
                                    count: tasks.length,
                                }).toUpperCase()}
                            </Text>
                            <Select
                                options={taskOptions}
                                value={selectedTaskOption}
                                onChange={handleSelectTaskOption}
                            />
                        </>
                    ) : (
                        <>
                            <Text style={{ marginBottom: theme.spacing.xxxs }} scale="bodyS700" color="secondary">
                                {newT('campaign.section.tasks', {
                                    defaultValue: 'TASK',
                                    count: tasks.length,
                                }).toUpperCase()}
                            </Text>
                            <Text scale="bodyM700" color="primary" style={{ marginBottom: theme.spacing.xxs }}>
                                {sidebarTask.title}
                            </Text>
                        </>
                    )}
                    <Text
                        style={{ marginTop: theme.spacing.xxs, marginBottom: theme.spacing.xxxs }}
                        scale="bodyS700"
                        color="secondary"
                    >
                        {newT('campaign.section.steps', 'STEPS').toUpperCase()}
                    </Text>
                    <Select options={stepOptions} value={selectedOption} onChange={handleSelectOption} />
                    <Text style={{ marginTop: theme.spacing.xxs }} scale="bodyS700" color="secondary">
                        {newT('sidebar.submission.status', 'STATUSES').toUpperCase()}
                    </Text>
                    <div className="status-options">
                        {singleTaskViews.map(singleTaskView => {
                            const isSelected = !selectedRegistrationView && singleTaskView === selectedSubmissionView
                            return (
                                <Button
                                    key={singleTaskView}
                                    width="100%"
                                    bg={isSelected ? theme.colors.grandis : theme.newColors.neutral50}
                                    textProps={{ color: theme.colors.black, textAlign: 'left' }}
                                    px={theme.spacing.xs}
                                    onClick={() => handleSubmissionClick(singleTaskView)}
                                >
                                    {`${SUBMISSION_VIEW_LABEL[singleTaskView]} (${getCounts(singleTaskView)})`}
                                </Button>
                            )
                        })}
                    </div>
                </div>
            ) : null}
        </Container>
    )
}

const NavSidebarContainer: React.FC<any> = props => {
    const dispatch = useDispatch()
    const showSidebar = useSelector(selectShowSidebar)
    const onShowSidebar = useCallback(() => {
        dispatch(setShowSidebar(true))
    }, [])
    const windowSize = useWindowSize()

    return (
        <>
            <NavSidebar {...props} show={showSidebar} />
            {!showSidebar && windowSize > 768 && (
                <ButtonFa
                    icon={faChevronRight}
                    iconSize="xs"
                    onClick={onShowSidebar}
                    mt={theme.spacing.s}
                    sx={{ position: 'sticky', top: '4em' }}
                    maxHeight="50px"
                />
            )}
        </>
    )
}

export const ToggleNavSidebarButton = () => {
    const dispatch = useDispatch()

    const onShowSidebar = useCallback(() => {
        dispatch(setShowSidebar(true))
    }, [])

    return <MobileNavSidebarButton icon={faChevronRight} iconSize="xs" onClick={onShowSidebar} maxHeight="50px" />
}

const MobileNavSidebarButton = styled(ButtonFa)`
    display: none;
    @media screen and (max-width: 768px) {
        display: flex;
    }
    width: 39.5px;
    min-width: 39.5px;
    height: 50px;
    padding: 16px 8px;
    justify-content: center;
    align-items: center;
`

export default NavSidebarContainer
